import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { backendService } from "@/services/backend.service";
import type { NurseLocationHistoryResponse } from "@/utils/models";

/** Trailing window, in days, over which a nurse's recent shifts are counted per location. */
export const NURSE_LOCATION_HISTORY_WINDOW_DAYS = 30;

/** A few minutes: shift history barely moves within a scheduling session, and the picker reopens often. */
const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * How many recent shifts a nurse has worked at each location. Read-only; drives the location picker's
 * "N recent shifts" subtitle and its "works most" ordering. Disabled (no request) when there is no nurse.
 *
 * A failed fetch degrades the picker to an unranked list, which is easy to miss — so the error is tracked
 * rather than swallowed, giving telemetry when the endpoint misbehaves in production.
 */
export const useNurseLocationHistory = (departmentId: string, nurseId: number | null | undefined, days: number) => {
	const { trackError } = useAnalytics();
	const query = useQuery<NurseLocationHistoryResponse>({
		queryKey: ["nurseLocationHistory", departmentId, nurseId, days],
		// `enabled` guarantees nurseId is non-null before the queryFn runs, so the cast is safe.
		queryFn: async () => (await backendService.getNurseLocationHistory(departmentId, nurseId as number, days)).data,
		enabled: !!departmentId && !!nurseId,
		staleTime: STALE_TIME_MS,
	});

	useEffect(() => {
		if (query.error) {
			trackError("nurse_location_history_fetch_failed", {
				department_id: departmentId,
				nurse_id: nurseId ?? null,
				error_message: query.error instanceof Error ? query.error.message : String(query.error),
			});
		}
	}, [query.error, trackError, departmentId, nurseId]);

	return query;
};
